import {
  QuizData, QuizSubmission, QuizResults,
  FileMetadata, FileOnlyUploadResponse, FileListResponse,
  GenerateQuizResponse, FlashcardSet, GenerateFlashcardRequest,
  GenerateFlashcardResponse, FlashcardSessionState,
  QuizHistoryResponse, QuizResultHistoryResponse, QuizWithResults
} from './types';
import { validateFileType, validateFileSize, generateCSRFToken } from '../utils/security';
import { getClientId } from '../utils/clientId';

class QuizAPI {
  private baseURL: string;
  // @ts-ignore
  private csrfToken: string;
  // @ts-ignore
  private clientId: string;

  constructor(baseURL: string = 'http://localhost:8000') {
    this.baseURL = baseURL;
    this.csrfToken = generateCSRFToken();
    this.clientId = getClientId();
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // If response doesn't contain valid JSON, use the default message
      }

      const error = new Error(errorMessage);
      (error as any).status = response.status;
      throw error;
    }

    return response.json();
  }

  async submitQuiz(submission: QuizSubmission): Promise<QuizResults> {
    const response = await fetch(`${this.baseURL}/api/submit-quiz`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // 'X-CSRF-Token': this.csrfToken,
        // 'X-Requested-With': 'XMLHttpRequest',
        'X-Client-ID': this.clientId,
      },
      body: JSON.stringify(submission),
    });

    return this.handleResponse<QuizResults>(response);
  }

  // Quiz History methods

  async getQuizHistory(): Promise<QuizHistoryResponse> {
    const response = await fetch(`${this.baseURL}/api/quizzes`, {
      method: 'GET',
      headers: {
        'X-Client-ID': this.clientId,
      },
    });

    return this.handleResponse<QuizHistoryResponse>(response);
  }

  async getQuizResultsHistory(quizId: string): Promise<QuizResultHistoryResponse> {
    const response = await fetch(`${this.baseURL}/api/quizzes/${quizId}/results`, {
      method: 'GET',
      headers: {
        'X-Client-ID': this.clientId,
      },
    });

    return this.handleResponse<QuizResultHistoryResponse>(response);
  }

  async getQuizForRetake(quizId: string): Promise<QuizWithResults> {
    const response = await fetch(`${this.baseURL}/api/quizzes/${quizId}`, {
      method: 'GET',
      headers: {
        'X-Client-ID': this.clientId,
      },
    });

    return this.handleResponse<QuizWithResults>(response);
  }

  async getQuizResultDetail(resultId: string): Promise<QuizResults> {
    const response = await fetch(`${this.baseURL}/api/quiz-results/${resultId}`, {
      method: 'GET',
      headers: {
        'X-Client-ID': this.clientId,
      },
    });

    return this.handleResponse<QuizResults>(response);
  }

  async healthCheck(): Promise<{ status: string }> {
    const response = await fetch(`${this.baseURL}/api/health`);
    return this.handleResponse<{ status: string }>(response);
  }

  // Utility method to check if backend is available
  async isBackendAvailable(): Promise<boolean> {
    try {
      await this.healthCheck();
      return true;
    } catch {
      return false;
    }
  }

  // File management methods

  async uploadFileOnly(file: File, onProgress?: (progress: number) => void): Promise<FileMetadata> {
    // Security validations
    if (!validateFileType(file)) {
      throw new Error('Invalid file type. Only PDF and TXT files are allowed.');
    }

    if (!validateFileSize(file, 5)) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', async () => {
        try {
          const response = new Response(xhr.response, {
            status: xhr.status,
            statusText: xhr.statusText,
          });

          const result = await this.handleResponse<FileOnlyUploadResponse>(response);
          resolve(result.file_metadata);
        } catch (error) {
          reject(error);
        }
      });

      xhr.addEventListener('error', () => {
        const error = new Error('Network error during file upload');
        reject(error);
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout'));
      });

      xhr.open('POST', `${this.baseURL}/api/files`);
      xhr.timeout = 60000; // 60 second timeout

      // Add security headers
      // xhr.setRequestHeader('X-CSRF-Token', this.csrfToken);
      // xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.setRequestHeader('X-Client-ID', this.clientId);

      xhr.send(formData);
    });
  }

  async listFiles(): Promise<FileMetadata[]> {
    const response = await fetch(`${this.baseURL}/api/files`, {
      method: 'GET',
      headers: {
        // 'X-CSRF-Token': this.csrfToken,
        // 'X-Requested-With': 'XMLHttpRequest',
        'X-Client-ID': this.clientId,
        // 'x-client-id': this.clientId,
      },
    });

    const result = await this.handleResponse<FileListResponse>(response);
    return result.files;
  }

  async getFileDetails(fileId: string): Promise<FileMetadata> {
    const response = await fetch(`${this.baseURL}/api/files/${fileId}`, {
      method: 'GET',
      headers: {
        // 'X-CSRF-Token': this.csrfToken,
        // 'X-Requested-With': 'XMLHttpRequest',
        'X-Client-ID': this.clientId,
      },
    });

    return this.handleResponse<FileMetadata>(response);
  }

  async generateQuizFromFile(fileId: string): Promise<QuizData> {
    const response = await fetch(`${this.baseURL}/api/files/${fileId}/generate-quiz`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // 'X-CSRF-Token': this.csrfToken,
        // 'X-Requested-With': 'XMLHttpRequest',
        'X-Client-ID': this.clientId,
      },
    });

    const result = await this.handleResponse<GenerateQuizResponse>(response);
    return result.quiz_data;
  }

  async deleteFile(fileId: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/api/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        // 'X-CSRF-Token': this.csrfToken,
        // 'X-Requested-With': 'XMLHttpRequest',
        'X-Client-ID': this.clientId,
      },
    });

    await this.handleResponse<{ message: string }>(response);
  }

  // Flashcard methods

  async generateFlashcardsFromFile(fileId: string, numCards: number = 10): Promise<FlashcardSet> {
    const request: GenerateFlashcardRequest = {
      file_id: fileId,
      num_cards: numCards
    };

    const response = await fetch(`${this.baseURL}/api/files/${fileId}/generate-flashcards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-ID': this.clientId,
      },
      body: JSON.stringify(request),
    });

    const result = await this.handleResponse<GenerateFlashcardResponse>(response);
    return result.flashcard_data;
  }

  async createFlashcardSession(flashcardSetId: string): Promise<FlashcardSessionState> {
    const response = await fetch(`${this.baseURL}/api/flashcard-sessions?flashcard_set_id=${flashcardSetId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-ID': this.clientId,
      },
    });

    return this.handleResponse<FlashcardSessionState>(response);
  }

  async updateFlashcardSession(sessionId: string, sessionData: FlashcardSessionState): Promise<FlashcardSessionState> {
    const response = await fetch(`${this.baseURL}/api/flashcard-sessions/${sessionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-ID': this.clientId,
      },
      body: JSON.stringify(sessionData),
    });

    return this.handleResponse<FlashcardSessionState>(response);
  }
}

// Create a singleton instance
const apiClient = new QuizAPI(import.meta.env.VITE_API_BASE_URL);

export default apiClient;
export { QuizAPI };